import { LAYOUT_TYPES } from "../Contexts/DimensionsContext";

/**
 * tapWordAnchor — where the tap word sits, in SCREEN pixels, for every popup.
 *
 * ── Why a published measurement and not a shared formula ──
 *
 * The intro popup is plain PIXI over the whole screen: its word is sized from
 * the popup background's width and placed from the background rect, and in
 * landscape its line is measured from the bottom of the slide card. Every other
 * popup is a SPINE: its word sits on the artist's bone, in spine units, at a
 * size that the spine's own scale then multiplies.
 *
 * The two had nothing in common, so they could only line up by luck — and the
 * spine ones carried a hand-tuned `buttonYOffset` per device, in four separate
 * controllers, tuned against total win rather than against the start page.
 * Mina, demo 2: "Make 'TAP TO START/CONTINUE' same size and position on all
 * screens. Copy from start page."
 *
 * Reproducing the intro's maths elsewhere would mean copying its background
 * fit, its card aspect and its two orientation configs — a second copy of a
 * layout that is still being tuned. So the intro simply PUBLISHES what it
 * measured, and the spine popups solve back to it at their own scale.
 *
 * That is safe here for one specific reason: the intro popup is mounted for the
 * whole session. `introMounted` is only ever set false when init FAILS (see
 * loadingProgressSlice), so it re-lays out on every resize and what is
 * published is never stale. If that ever changes, this goes stale silently —
 * hence `getTapWordAnchor` returning null rather than a guess, and every
 * consumer keeping its own behaviour when it does.
 */

/**
 * How far BELOW its measured line the tap word sits, PER LAYOUT.
 *
 * Keyed on LAYOUT_TYPES — the resolved layout from DimensionsContext, which is
 * what every other responsive table in this game keys on. It is finer than
 * device + orientation because it also splits desktop four ways, and it is
 * exported for exactly this purpose.
 *
 * The value is a multiple of the word's own font size, not a pixel count. The
 * popups run from a small phone in landscape to an ultra-wide monitor, and a
 * fixed nudge that reads as "a couple of pixels" on one is invisible on
 * another. Scaled by the font, the gap keeps its proportion.
 *
 * Why it exists: on a phone in landscape the word sat closer to the value than
 * the "0X BET" line above it did, so the number looked pushed down rather than
 * centred between the two. Vladimir, 2026-09-22: "push it downwards on all of
 * them, even the intro popup just a couple pixels so xbet and tap to continue
 * have same space from the number" — landscape only, at first.
 *
 * ── A DEFAULT for every device, and each layout's own extra ON TOP ──
 *
 * TAP_WORD_NUDGE_DEFAULT applies to every layout (an unknown one included).
 * TAP_WORD_NUDGE holds what a layout adds to it — 0 unless that layout needs
 * more (Vladimir, 2026-09-25 / 2026-10-09: "every single device should have
 * that, and then on top of that we have custom one").
 *
 * ONE table, applied at the intro's own placement AND to what it publishes, so
 * the start page and every spine popup move together by construction. Raising a
 * value pushes the word further from the value above it.
 */
export const TAP_WORD_NUDGE_DEFAULT = 1.2;

export const TAP_WORD_NUDGE = {
  [LAYOUT_TYPES.MOBILE_SMALL_PORTRAIT]: 0,
  [LAYOUT_TYPES.MOBILE_SMALL_LANDSCAPE]: 0.6, // a bit lower (2026-10-09)
  [LAYOUT_TYPES.MOBILE_PORTRAIT]: 0,
  // Wide phones (~2.1:1): the dialogs are sized by symbol size, which is
  // height-limited there, so they draw relatively bigger than on the small
  // phones and their value sat 18-21 px lower against this line — glued to
  // the tap word. +1.3 moves the line ~14-17 px down (2026-10-09).
  [LAYOUT_TYPES.MOBILE_LANDSCAPE]: 1.3,
  [LAYOUT_TYPES.TABLET_PORTRAIT]: 0,
  // [LAYOUT_TYPES.TABLET_LANDSCAPE]: 0,
  [LAYOUT_TYPES.TABLET_LANDSCAPE]: 1.3, // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29)
  // Small laptops: the word sat close under the value (2026-10-09).
  [LAYOUT_TYPES.SMALL_DESKTOP]: 0.6,
  [LAYOUT_TYPES.DESKTOP]: 0,
  [LAYOUT_TYPES.WIDE_DESKTOP]: 0,
  [LAYOUT_TYPES.ULTRA_WIDE_DESKTOP]: 0,
};

/**
 * The nudge in screen pixels: the default, plus the layout's own extra.
 *
 * @param {number} fontSize The word's rendered size, in screen pixels.
 * @param {string} layout   A LAYOUT_TYPES key, from DimensionsContext.
 */
export const tapWordNudge = (fontSize, layout) =>
  (fontSize > 0 ? fontSize : 0) *
  (TAP_WORD_NUDGE_DEFAULT + Math.max(0, TAP_WORD_NUDGE[layout] ?? 0));

let anchor = null;

/**
 * Called by the intro popup at the end of every layout pass.
 *
 * @param {number} y        The word's centre, in screen pixels.
 * @param {number} fontSize The size it is actually rendered at, in screen px.
 */
export const publishTapWordAnchor = ({ y, fontSize }) => {
  if (!Number.isFinite(y) || !(fontSize > 0)) return;
  anchor = { y, fontSize };
};

/**
 * The last measured intro geometry, or null before the intro has laid out.
 *
 * Null is a real answer and must be handled: a popup that opens before the
 * intro has measured — which nothing currently does, since the intro is the
 * first thing on screen — keeps whatever its own rig gave it.
 */
export const getTapWordAnchor = () => anchor;

/** Test seam. */
export const resetTapWordAnchor = () => {
  anchor = null;
};
